import { io } from 'socket.io-client';
import { getCookie } from './local-storage.util';
import { Subject, distinctUntilChanged, tap } from 'rxjs';
import { EventRoom, UpdateWSEventStatePayload, WEBSOCKET_EVENTS, constructRoomId } from '@/store/modules/websocket/websocket.types';
import store from '@/store/store';

function log(message: string) {
    console.log(message);
}

// JIRA: SUMDEV-2964 - Extended timeout settings for long-running operations
const SocketClient = io(process.env.VUE_APP_BACKEND_HOST_NAME, {
    autoConnect: false,
    path: process.env.VUE_APP_WEBSOCKET_PATH,
    transports: ['websocket'],
    extraHeaders: {
        'X-Xsrf-Token': getCookie('XSRF-TOKEN') || '',
    },
    // Timeout settings to handle long-running jobs (30+ minutes)
    timeout: 60000,           // 1 minute - connection timeout
    reconnection: true,       // Enable auto-reconnection
    reconnectionDelay: 2000,  // Wait 2 seconds before reconnecting
    reconnectionAttempts: 10  // Try up to 10 times
});

const activeEventRooms: EventRoom[] = [];

// Guard to ensure system-level socket handlers are registered exactly once
// across the singleton SocketClient lifetime.
let handlersRegistered = false;

function registerRoomListeners(room: EventRoom) {
    SocketClient.emit(WEBSOCKET_EVENTS.JOIN_ROOM, {
        roomId: constructRoomId(room),
    });
    SocketClient.off(room.event);
    SocketClient.on(room.event, async (message: any) => {
        await store.dispatch(
            'websocket/wsUpdateEvent',
            <UpdateWSEventStatePayload>{ event: room.event, message }
        );
    });
}

const joinEventRoom$ = new Subject<EventRoom>();
joinEventRoom$.pipe(
    distinctUntilChanged(),
    tap((room: EventRoom) => {
        const idx = activeEventRooms.findIndex(r => r.event === room.event);
        if (idx !== -1) {
            activeEventRooms[idx] = room; // Update to current project
        } else {
            activeEventRooms.push(room);
        }
    }),
).subscribe((room: EventRoom) => {
    registerRoomListeners(room);
});

export function useWebsocket(vue: Vue) {
    const isConnected = () => vue.$store.getters['websocket/isConnected'];
    const socketConnect = () => vue.$store.dispatch('websocket/wsConnect');
    const socketDisconnect = () => vue.$store.dispatch('websocket/wsDisconnect');

    const onConnected = () => {
        if (!isConnected()) {
            socketConnect();
            log(`WS connected`);
        }
    };

    const onDisconnected = () => {
        if (isConnected()) {
            socketDisconnect();
            // Only remove room-specific listeners, keep system listeners (connect, reconnect, etc.)
            activeEventRooms.forEach((room: EventRoom) => {
                SocketClient.off(room.event);
            });
            log(`WS disconnected`);
        }
    };

    const connectToWs = () => {
        return new Promise((resolve, _) => {
            if (isConnected()) {
                resolve(true);
                return;
            }

            // Pass userId in auth for server-side session validation
            const accountState = store.state.account;
            const accountDetail = (accountState as any)?.getAccount?.data;
            if (accountDetail && accountDetail.id) {
                SocketClient.auth = { userId: accountDetail.id };
            }

            // Register system-level handlers only once to prevent accumulation
            // on the module-level singleton across multiple connectToWs() calls.
            if (!handlersRegistered) {
                handlersRegistered = true;

                SocketClient.on('connect', onConnected);
                SocketClient.on('disconnect', onDisconnected);

                SocketClient.on('connect_error', (error: Error) => {
                    log(JSON.stringify(error));
                    log(`WebSocket connection failed: ${error.message}`);
                });

                SocketClient.on('reconnect_attempt', () => {
                    log('Reconnection attempt...');
                });

                SocketClient.on('reconnect', () => {
                    log('Successfully reconnected');
                    onConnected();
                    // Re-join all active rooms and re-register event listeners
                    activeEventRooms.forEach(registerRoomListeners);
                    // Notify the store so components can refetch state missed during disconnect
                    store.dispatch('websocket/wsReconnect');
                });
            }

            SocketClient.open();
            SocketClient.once('connect', () => resolve(true));
        });
    };

    const disconnectWs = () => {
        return new Promise((resolve, _) => {
            if (!isConnected()) {
                resolve(true);
                return;
            }
            SocketClient.close();
            SocketClient.off('connect', onConnected);
            SocketClient.off('disconnect', onDisconnected);
            SocketClient.once('disconnect', resolve);
        });
    };

    const joinRoom = (room: EventRoom) => {
        joinEventRoom$.next(room);
    };

    return {
        isConnected,
        connectToWs,
        disconnectWs,
        SocketClient,
        joinRoom
    };
}
